// @flow
import { alerts, devices, feedback } from '~/core/constants';
import { BOOST_TARGET, MANUAL_TARGET_SETTING } from '~/core/constants/app-settings';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
    GET_TEMPERATURE_HISTORY,
    SELECT_PERIOD,
    SET_TARGET_TEMPERATURE,
    SET_TARGET_TEMPERATURE_UPDATE_PRODUCT,
    TOGGLE_READY_BY
} from './constants';
import { getModeForProduct, getTypeForProduct } from '~/core/state/products/selectors';
import { getIsReadyByEnabled } from '~/core/state/products/heating/selectors';
import {
    getTemperatureHistory,
    getTemperatureHistorySuccess,
    setHeatingTargetSuccess,
    setHeatingTargetUpdateProduct
} from './actions';
import type {
    ChangeReadyByStatusAction,
    HeatingHistoryAction,
    HeatingTargetAction,
    HistoryPeriodChangeAction
} from './types';

import { buildKey } from '~/core/utils/app-settings';
import { getCurrentTimeWithTimezone } from '~/core/state/hub/selectors';
import { getHeatingHistory } from '~/core/api/history/history.api';
import { getStartEndTimeFromPeriod } from '~/core/utils/periods';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import { noop } from 'lodash/fp';
import { patchProduct } from '~/core/api/products/products.api';
import { requestAlert } from '~/core/state/alerts/actions';
import { setAppSettings } from '~/core/state/app-settings/actions';
import { undoProductChange } from '../actions';

export function* heatingTargetSaga({
    payload: { productId, target }
}: HeatingTargetAction): Generator<Effect | noop, *, *> {
    const props = { id: productId };
    const productType = yield select(getTypeForProduct, props);
    const mode = yield select(getModeForProduct, props);
    const patch: Object = { target: target };

    if (mode === devices.deviceModes.OFF) {
        patch.mode = devices.deviceModes.MANUAL;
    } else if (mode === devices.deviceModes.MANUAL) {
        yield put(setAppSettings({ [buildKey(MANUAL_TARGET_SETTING, productId)]: target }));
    } else if (mode === devices.deviceModes.BOOST) {
        yield put(setAppSettings({ [buildKey(BOOST_TARGET, productId)]: target }));
    }

    yield put(setHeatingTargetUpdateProduct({ productId, target }));
    const [, error] = yield call(handleApi(patchProduct), patch, productType, productId);

    if (error) {
        yield put(undoProductChange(SET_TARGET_TEMPERATURE_UPDATE_PRODUCT));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
        return;
    }

    yield put(setHeatingTargetSuccess({ productId }));
}

function* watchHeatingTargetSaga(): Generator<Effect, *, *> {
    yield takeLatest([SET_TARGET_TEMPERATURE], handleSaga(heatingTargetSaga));
}

export function* heatingHistorySaga({
    payload: { productId, period, start, end }
}: HeatingHistoryAction): Generator<noop | Effect, *, *> {
    const [result, error] = yield call(
        handleApi(getHeatingHistory),
        productId,
        start,
        end,
        period.timeUnit,
        period.rate
    );

    if (error) {
        if (error.code === 404) {
            yield put(
                getTemperatureHistorySuccess({
                    data: [],
                    productId,
                    period,
                    missingData: true
                })
            );
            return;
        }

        return yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }

    const { data } = result;

    yield put(
        getTemperatureHistorySuccess({
            data,
            productId,
            period,
            missingData: false
        })
    );
}

function* watchHeatingHistorySaga(): Generator<Effect, *, *> {
    yield takeLatest([GET_TEMPERATURE_HISTORY], handleSaga(heatingHistorySaga));
}

function* historyPeriodChangeSaga({
    payload: { productId, period }
}: HistoryPeriodChangeAction): Generator<Effect, *, *> {
    const currentHubTime = yield select(getCurrentTimeWithTimezone);
    const range = getStartEndTimeFromPeriod(period, currentHubTime);
    yield put(getTemperatureHistory({ productId, period, ...range }));
}

function* watchHistoryPeriodSelection(): Generator<Effect, *, *> {
    yield takeLatest([SELECT_PERIOD], handleSaga(historyPeriodChangeSaga));
}

export function* changeReadyByStatusSaga({
    payload: { productId }
}: ChangeReadyByStatusAction): Generator<Effect | noop, *, *> {
    const props = { id: productId };

    const nextValue = yield select(getIsReadyByEnabled, props);
    const productType = yield select(getTypeForProduct, props);

    const [, error] = yield call(handleApi(patchProduct), { optimumStart: nextValue }, productType, productId);

    if (error) {
        yield put(undoProductChange(TOGGLE_READY_BY));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }
}

function* watchReadyByStatusSaga(): Generator<Effect, *, *> {
    yield takeLatest(TOGGLE_READY_BY, changeReadyByStatusSaga);
}

export default [watchReadyByStatusSaga, watchHeatingTargetSaga, watchHeatingHistorySaga, watchHistoryPeriodSelection];



// WEBPACK FOOTER //
// ./src/core/state/products/heating/sagas.js